import { exportPrivateCryptoKeyToPem, exportPublicCryptoKeyToPem } from '@lucidlink/ts-crypto/crypto';
import { assertCanHandle, isNil } from '@lucidlink/tscore';
import { createAccessModeSynchronizer, FileSystemAccessMode } from '#access-mode';
import { createAuthorizer } from '#auth/operation-authorizer';
import { ServerSession, ServerSessionProxy } from '#auth/server-session';
import { createCompressorStore } from '#block-store/compressor-store';
import { createCryptoStore } from '#block-store/crypto-store';
import { createLogStore } from '#block-store/log-store';
import { Logging } from '#common/logging';
import { Trace } from '#common/trace';
import { InvalidStateError, MAX_SUPPORTED_FS_VERSION, MIN_SUPPORTED_FS_VERSION, NotImplementedError, } from '#common/types';
import { getVersionString, stringifyReplacerForBigInt, toISOStringWithTimeZoneOffset } from '#common/util';
import { getDeviceHostName } from '#common/util/device-host-name';
import { CommonConfigKeyHelper, createConfigManager, getSettingFromList, SettingsCache } from '#config';
import { MissingConfigKey, SettingName, SettingTarget, } from '#config/types';
import { createAdminCryptoProvider } from '#crypto';
import { createDirectLinksManager } from '#direct-links';
import { createDataStores, createExternalFilesManager, ExternalFilesConfigValidator } from '#external-files';
import { createFileNodeManager, getFileStoreBuilder } from '#file-store';
import { createFileSystem } from '#filesystem';
import { UserActivityTracker } from '#filesystem/user-activity-tracker';
import { StatsFlusher } from '#hub-stats';
import { createKeyRangeLockingClient } from '#key-range-locking';
import { createMemKeyStore } from '#key-store';
import { MetaStoreProxy } from '#metastore';
import { AtomicCounterProxy, createEpochManager, createNode, EpochProxy } from '#node';
import { buildClientObjectStore } from '#object-store';
import { createHttpStore } from '#object-store/http';
import { createFilePermissionsManager, createFilePermissionsSynchronizer, createPrincipalCryptoProvider, } from '#permissions';
import { createSnapshotManager } from '#snapshot-manager';
import { createSnapshotSchedulerClient } from '#snapshot-schedules';
import { createLoginTelemetryRecord, createTelemetryClient } from '#telemetry';
import { getEnvInfo } from '#telemetry/get-env';
import { TelemetryName } from '#telemetry/types';
import { createClientWasm, logLevelToWasmLogLevel, } from '#wasm/modules';
import { createWebSocketClient } from '#websocket/client';
import { buildObjectStoreConfig } from './object-store-config-builder';
import { FilespaceVersionError, FileSystemReadOnlyException, InvalidFilespaceStateError } from './types/errors';
import { FilespaceState, SNAPSHOT_CURRENT_VIEW } from './types/filespace';
import { LOST_AND_FOUND_MIN_FILESPACE_VERSION } from './types/init-params';
import { FilespaceStats } from './types/stats';
import { NullTelemetry } from './types/telemetry';
var OperationMode;
(function (OperationMode) {
    OperationMode["READ"] = "read";
    OperationMode["READ_WRITE"] = "readWrite";
})(OperationMode || (OperationMode = {}));
export const createFilespace = (params) => {
    return new FilespaceImpl(params.application, params.platformInfo, params.storage ?? createMemKeyStore(), params.logTelemetry ?? NullTelemetry);
};
const REQUIRED_LINK_SETTINGS = [
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.BUCKET_NAME,
    },
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.OBJECT_PREFIX,
    },
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.BLOCK_SIZE,
    },
    {
        target: SettingTarget.FS_INIT,
        name: SettingName.FS_VERSION,
    },
    {
        target: SettingTarget.FILE_STORE,
        name: SettingName.STUCK_STATE_INTERVAL_SETTING,
    },
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.OBJECT_STORE,
    },
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.REGION,
    },
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.ENDPOINT,
    },
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.LOCATION_CONSTRAINT,
    },
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.VIRTUAL_ADDRESSING,
    },
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.PROTOCOL,
    },
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.PROVIDER,
    },
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.STORAGE_CLASS,
    },
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.ACCESS_KEY_ID,
    },
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.SECRET_KEY,
    },
    {
        target: SettingTarget.FS_CONFIG,
        name: SettingName.OBJECT_STORE_CREDENTIALS_TYPE,
    },
    {
        target: SettingTarget.OBJECT_SCHEDULER,
        name: SettingName.MAX_DOWNLOAD_RATE,
    },
    {
        target: SettingTarget.OBJECT_SCHEDULER,
        name: SettingName.MAX_UPLOAD_RATE,
    },
    {
        target: SettingTarget.FILE_SYSTEM,
        name: SettingName.FS_ACCESS_MODE,
    },
];
export class FilespaceImpl {
    application;
    platformInfo;
    storage;
    logTelemetry;
    webSocketClient;
    config;
    fs;
    permissions;
    principalCryptoProvider;
    permissionsSynchronizer;
    snapshots;
    filespaceId;
    objectStore;
    httpStore;
    fileStore;
    statsFlusher;
    bucketName;
    objectPrefix;
    serverSession;
    idService;
    telemetry;
    authorizer;
    snapshotSchedulerClient;
    node;
    nodeId;
    state = FilespaceState.UNLINKED;
    log = Logging.getLogger('Filespace');
    wasm;
    keyRangeLockingClient;
    accessModeSynchronizer;
    statistics = new FilespaceStats();
    fileNodeManager;
    directLinksManager;
    dataStores;
    externalFilesMgr;
    trace;
    settingsCache;
    linkedMode = OperationMode.READ_WRITE;
    constructor(application, platformInfo, storage, logTelemetry = NullTelemetry) {
        this.application = application;
        this.platformInfo = platformInfo;
        this.storage = storage;
        this.logTelemetry = logTelemetry;
    }
    getLogLevel(name) {
        return isNil(name) ? Logging.getRootLogLevel() : Logging.getLogger(name).level;
    }
    async setLogLevel(level, name) {
        this.log.info(`Setting ${name ?? 'root'} log level to ${level}`);
        if (isNil(name)) {
            Logging.setLevel(level);
            await this.setUpWasmLogging();
        }
        Logging.setLevel(level, name);
        await this.wasm?.setLogLevel(logLevelToWasmLogLevel(level), name);
    }
    async link(idService, remoteEndpoint, params, settings) {
        const linkStart = performance.now();
        this.setLinkingState(params);
        this.idService = idService;
        try {
            await this.doLink(remoteEndpoint, params, settings);
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof Error);
            this.log.error({ errorMessage: err.message, 'filespace.fsId': params.filespaceId }, 'Failed to link to Filespace!');
            await this.tearDownSynchronizers();
            await this.tearDown();
            this.state = FilespaceState.UNLINKED;
            throw err;
        }
        this.setLinkedState(params);
        this.sendLoginTelemetry(params.principalName, performance.now() - linkStart, this.application);
    }
    async unlink() {
        await this.doUnlink();
    }
    validateLinkedState(operationName) {
        if (this.state !== FilespaceState.LINKED) {
            throw new InvalidFilespaceStateError(`Cannot get ${operationName} - Filespace is not linked`);
        }
    }
    requireMinOperationMode(operationName, mode) {
        this.validateLinkedState(operationName);
        if (mode === OperationMode.READ_WRITE && this.linkedMode === OperationMode.READ) {
            throw new FileSystemReadOnlyException(`${operationName} is not available in read-only mode.`);
        }
    }
    filePermissionsManager() {
        this.requireMinOperationMode('PermissionsManager', OperationMode.READ);
        return this.permissions;
    }
    events() {
        this.requireMinOperationMode('Events', OperationMode.READ);
        return this.wasm.events();
    }
    snapshotManager() {
        this.requireMinOperationMode('SnapshotManager', OperationMode.READ_WRITE);
        return this.snapshots;
    }
    fileSystem(snapshotId) {
        if (!isNil(snapshotId) && snapshotId !== SNAPSHOT_CURRENT_VIEW) {
            throw new NotImplementedError('Snapshots are currently unsupported.');
        }
        this.requireMinOperationMode('FileSystem', OperationMode.READ_WRITE);
        return this.fs;
    }
    externalFilesManager() {
        this.requireMinOperationMode('ExternalFilesManager', OperationMode.READ_WRITE);
        return this.externalFilesMgr;
    }
    configManager() {
        this.requireMinOperationMode('ConfigManager', OperationMode.READ_WRITE);
        return this.config;
    }
    snapshotScheduler() {
        this.requireMinOperationMode('SnapshotScheduler', OperationMode.READ_WRITE);
        return this.snapshotSchedulerClient;
    }
    directLinks() {
        this.requireMinOperationMode('DirectLinks', OperationMode.READ_WRITE);
        return this.directLinksManager;
    }
    stats() {
        return this.statistics;
    }
    status() {
        return { nodeId: this.nodeId, state: this.state };
    }
    isTracing() {
        return !isNil(this.trace);
    }
    recordTrace() {
        if (this.isTracing()) {
            return;
        }
        this.trace = new Trace();
        this.stats().setTrace(this.trace);
        this.log.warn('Tracing events started...');
    }
    stopTrace() {
        if (!this.isTracing()) {
            return [];
        }
        const traceEvents = this.trace.getTraceEvents();
        this.stats().stopTrace();
        this.trace = undefined;
        this.log.warn('Tracing events stopped.');
        return traceEvents;
    }
    async setUpWebSocketClient(remoteEndpoint) {
        this.webSocketClient = createWebSocketClient();
        await this.webSocketClient.connect(remoteEndpoint);
        this.statistics.network = this.webSocketClient.stats();
    }
    sendLoginTelemetry(username, startTimeMs, application) {
        const localTime = new Date();
        const utcTime = new Date(localTime.toUTCString());
        const startTime = `${startTimeMs.toFixed(0)}ms`;
        const version = getVersionString();
        const localTimeStr = toISOStringWithTimeZoneOffset(localTime);
        const logLevel = Logging.getRootLogLevel();
        const utcTimeStr = utcTime.toISOString();
        const userAgent = getEnvInfo();
        const telemetryRecord = createLoginTelemetryRecord(startTime, version, localTimeStr, username, logLevel, utcTimeStr, application, userAgent);
        const loginTelementryJson = JSON.stringify(telemetryRecord, stringifyReplacerForBigInt);
        this.telemetry.sendMetricsRaw(TelemetryName.LOG_IN, loginTelementryJson);
    }
    setLinkedState(params) {
        this.state = FilespaceState.LINKED;
        this.log.info({ 'filespace.fsId': params.filespaceId }, 'Successfully linked to Filespace.');
    }
    setLinkingState(params) {
        this.log.info({ 'filespace.fsId': params.filespaceId }, 'Linking to Filespace');
        if (this.state !== FilespaceState.UNLINKED) {
            throw new InvalidFilespaceStateError('Filespace already linked. Unlink first to link to another one.');
        }
        this.state = FilespaceState.LINKING;
    }
    setUpFilePermissionsManager() {
        const permissionsModule = this.wasm.permissions();
        this.permissions = createFilePermissionsManager(permissionsModule, this.idService, this.authorizer, this.wasm.keyValueHelpers(), this.linkedMode === OperationMode.READ);
        this.statistics.permissions = this.permissions.stats();
    }
    setUpSnapshotManager() {
        this.snapshots = createSnapshotManager(this.filespaceId, this.webSocketClient, this.wasm.keyValueHelpers(), this.wasm.utils(), this.serverSession, this.authorizer);
    }
    setUpSnapshotScheduler() {
        this.snapshotSchedulerClient = createSnapshotSchedulerClient(this.wasm.snapshotScheduler(), this.wasm.keyValueHelpers(), this.keyRangeLockingClient, this.nodeId, this.authorizer);
    }
    setUpDirectLinksManager() {
        this.directLinksManager = createDirectLinksManager(this.wasm.fileSystem());
    }
    setUpDataStores(adminCryptoProvider, configValidator, commonConfigKeyHelper) {
        this.dataStores = createDataStores(this.wasm.dataStores(), this.wasm.tuple(), this.wasm.keyValueHelpers(), this.authorizer, this.fileNodeManager, this.config, commonConfigKeyHelper, configValidator, adminCryptoProvider, this.keyRangeLockingClient, this.nodeId, this.logTelemetry);
    }
    setUpExternalFilesManager() {
        this.externalFilesMgr = createExternalFilesManager(this.fs, this.dataStores);
    }
    buildFileStore(fileNodeManager, commonConfigKeyHelper, statsFlusher) {
        const builder = getFileStoreBuilder(this.wasm.fileStore(), fileNodeManager);
        const logStore = createLogStore({
            objectStore: this.objectStore,
            httpStore: this.httpStore,
            commonConfigKeyHelper,
            fileNodeManager,
            logStoreWasm: this.wasm.logStore(),
            metaStore: this.wasm.metaStore(),
            tupleWasm: this.wasm.tuple(),
            keyValueHelpers: this.wasm.keyValueHelpers(),
            bucketName: this.bucketName,
            objectPrefix: this.objectPrefix,
            nodeId: this.nodeId,
            statsFlusher,
        });
        builder.addStage((_) => {
            return logStore;
        });
        builder.addStage((prevStage) => createCryptoStore(prevStage, this.wasm.utils()));
        builder.addStage((prevStage) => createCompressorStore(prevStage, this.wasm.compressor(), this.wasm.utils()));
        return builder.build();
    }
    setUpFileNodeManager(settings) {
        this.fileNodeManager = createFileNodeManager(this.wasm.keyValueHelpers(), this.wasm.metaStore(), settings?.metadataCacheTtlMs);
    }
    async setUpFileSystem(params, adminCryptoProvider, configValidator, settingsCache, commonConfigKeyHelper, fsVersion, settings) {
        const fileSystemModule = this.wasm.fileSystem();
        const userActivityTracker = this.createUserActivityTracker(params);
        this.statsFlusher = new StatsFlusher(this.wasm.metaStore(), settings?.ioStatsIntervalMs);
        this.fileStore = this.buildFileStore(this.fileNodeManager, commonConfigKeyHelper, this.statsFlusher);
        this.fs = createFileSystem({
            fsModule: fileSystemModule,
            fileStore: this.fileStore,
            fileNodeManager: this.fileNodeManager,
            commonConfigKeyHelper,
            userActivityTracker,
            configValidator,
            settingsCache,
            logStoreWasm: this.wasm.logStore(),
            tupleWasm: this.wasm.tuple(),
            utilsWasm: this.wasm.utils(),
            keyValueHelpers: this.wasm.keyValueHelpers(),
            dataStores: this.dataStores,
            adminCryptoProvider,
            metaStore: this.wasm.metaStore(),
            nodeId: this.nodeId,
            fsVersion,
            fsStatsIntervalMs: settings?.fsStatsIntervalMs,
            statsFlusher: this.statsFlusher,
            logTelemetry: this.logTelemetry,
        });
        this.statistics.fs = this.fs.stats();
        await this.statsFlusher.load();
        this.statsFlusher.start();
    }
    createConfigValidator(settingsCache) {
        return new ExternalFilesConfigValidator(settingsCache);
    }
    async doLink(remoteEndpoint, params, settings) {
        this.linkedMode = settings?.readOnlyMode ? OperationMode.READ : OperationMode.READ_WRITE;
        await this.setUp(params.filespaceId, remoteEndpoint, params.principalName, params.decryptedWorkspaceCommonKey);
        const linkSettings = await this.config.get(REQUIRED_LINK_SETTINGS);
        const fsVersion = this.getAndValidateFsVersion(linkSettings);
        this.setWasmFilespaceGlobals(linkSettings, fsVersion);
        const userCrypto = this.getUserCrypto(params);
        this.principalCryptoProvider = this.setUpPrincipalCryptoProvider(userCrypto);
        const nodeId = await this.node.getNodeIndex();
        const principalsCryptoKeys = await this.principalCryptoProvider.getCryptoKeys();
        const principalsCrypto = principalsCryptoKeys.principalCrypto;
        const isAdmin = principalsCryptoKeys.isAdmin;
        await this.linkFileSystem(params, nodeId, principalsCrypto, linkSettings, isAdmin);
        await this.setUpObjectStore(linkSettings);
        this.setUpWasmFileSystemModules();
        this.setupSynchronizers(this.principalCryptoProvider, this.wasm.permissions(), settings);
        const adminCrypto = principalsCrypto.find((principalCrypto) => principalCrypto.principalId === this.idService.getAdminGroupId());
        await this.setUpDataStoresAndFilesystem(params, fsVersion, adminCrypto, settings);
        this.setUpFilePermissionsManager();
        this.setUpSnapshotManager();
        this.setUpSnapshotScheduler();
        this.setUpDirectLinksManager();
        this.setUpExternalFilesManager();
    }
    async setUpDataStoresAndFilesystem(params, fsVersion, adminCrypto, settings) {
        const adminCryptoProvider = createAdminCryptoProvider(this.idService);
        this.setUpFileNodeManager(settings);
        this.settingsCache = new SettingsCache(this.config);
        const configValidator = this.createConfigValidator(this.settingsCache);
        const commonConfigKeyHelper = new CommonConfigKeyHelper(this.config);
        this.setUpDataStores(adminCryptoProvider, configValidator, commonConfigKeyHelper);
        await this.setUpFileSystem(params, adminCryptoProvider, configValidator, this.settingsCache, commonConfigKeyHelper, fsVersion, settings);
        await this.injectAdminCrypto(fsVersion, adminCrypto);
    }
    async injectAdminCrypto(fsVersion, adminCrypto) {
        if (fsVersion < LOST_AND_FOUND_MIN_FILESPACE_VERSION || isNil(adminCrypto)) {
            return;
        }
        try {
            await this.wasm.fileSystem().setAdminCrypto(adminCrypto.decryptedPrivateRsaKeyPem);
            this.log.info('EventStream: admin injected the admin-group key for Lost&Found rescue-key recovery.');
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof Error);
            this.log.error({ errorMessage: err.message }, 'Failed to inject the admin-group rescue key.');
        }
    }
    setWasmFilespaceGlobals(linkSettings, fsVersion) {
        this.validateFileSystemAccessMode(linkSettings);
        const blockSize = this.getBlockSizeSetting(linkSettings);
        this.wasm.setFilespaceGlobals({ fsVersion, blockSize });
    }
    setupSynchronizers(principalCryptoProvider, permissionsWasm, settings) {
        this.permissionsSynchronizer = createFilePermissionsSynchronizer(principalCryptoProvider, permissionsWasm);
        this.permissionsSynchronizer.start(settings?.permissionsRefetchIntervalMs);
        this.setupAccessModeSynchronizer(settings?.accessModeCheckIntervalMs);
    }
    setupAccessModeSynchronizer(intervalMs) {
        this.accessModeSynchronizer = createAccessModeSynchronizer(this.config, this.onReadOnlyDetected.bind(this));
        this.accessModeSynchronizer.start(intervalMs);
    }
    onReadOnlyDetected() {
        if (this.state !== FilespaceState.LINKED) {
            return;
        }
        if (this.linkedMode === OperationMode.READ) {
            return;
        }
        this.log.warn('FileSystem switched to ReadOnly mode - unlinking...');
        void this.doUnlink();
    }
    getUserCrypto(params) {
        return {
            principalId: params.principalId,
            decryptedPrivateRsaKeyPem: params.decryptedPrincipalPrivateKeyPem,
        };
    }
    createUserActivityTracker(params) {
        return new UserActivityTracker(this.nodeId, params.principalName, this.wasm.metaStore(), this.wasm.keyValueHelpers());
    }
    setUpWasmFileSystemModules() {
        this.wasm.setUpFileSystemModules(this.createGetObjectHandler(), this.getPrincipalsRsaKeys);
    }
    async doUnlink() {
        if (this.state !== FilespaceState.LINKED) {
            throw new InvalidFilespaceStateError('Filespace not currently linked.');
        }
        this.state = FilespaceState.UNLINKING;
        await this.tearDownFileStore();
        await this.tearDownSynchronizers();
        try {
            await this.wasm.filespace().unlink();
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof Error);
            this.log.error({ 'filespace.fsId': this.filespaceId, errorMessage: err.message }, 'Error while unlinking from Filespace!');
        }
        const filespaceId = this.filespaceId;
        await this.tearDown();
        this.state = FilespaceState.UNLINKED;
        this.log.info({ 'filespace.fsId': filespaceId }, 'Successfully unlinked.');
    }
    getAndValidateFsVersion(bootstrapSettings) {
        const fsVersionSetting = this.getSettingFromList(bootstrapSettings, SettingName.FS_VERSION);
        const fsVersion = fsVersionSetting.effective().asUint32();
        if (fsVersion < MIN_SUPPORTED_FS_VERSION) {
            throw new FilespaceVersionError(`Incompatible Filespace version! Client minimum supported version: ${MIN_SUPPORTED_FS_VERSION}, Filespace version: ", ${fsVersion}`);
        }
        if (fsVersion > MAX_SUPPORTED_FS_VERSION) {
            throw new FilespaceVersionError(`Incompatible Filespace version! Client maximum supported version: ${MAX_SUPPORTED_FS_VERSION}, Filespace version: ", ${fsVersion}`);
        }
        return fsVersion;
    }
    validateFileSystemAccessMode(bootstrapSettings) {
        const accessModeSetting = this.getSettingFromList(bootstrapSettings, SettingName.FS_ACCESS_MODE);
        const accessMode = accessModeSetting.effective().asUint32();
        if (accessMode === FileSystemAccessMode.READ_ONLY && this.linkedMode !== OperationMode.READ) {
            throw new FileSystemReadOnlyException();
        }
    }
    async linkFileSystem(params, nodeId, principalsCrypto, bootstrapSettings, isAdmin) {
        this.log.debug({ 'filespace.fsId': this.filespaceId }, 'Setting up filesystem...');
        this.log.debug({ 'filespace.fsId': this.filespaceId }, 'Linking the wasm part...');
        await this.wasm.filespace().link({
            commonParams: this.buildWasmCommonParams(params, nodeId),
            fileSystemParams: this.buildWasmFileSystemParams(params),
            permissionManagerParams: this.buildWasmPermissionManagerParams(principalsCrypto, isAdmin),
            logStoreParams: this.buildWasmLogStoreParams(bootstrapSettings),
            fileStoreParams: this.buildWasmFileStoreParams(bootstrapSettings),
            auditorParams: this.buildWasmAuditorParams(params),
        });
        this.log.debug({ 'filespace.fsId': this.filespaceId }, 'Done. Filesystem setup finished.');
    }
    buildWasmCommonParams(params, nodeId) {
        return {
            workspaceId: params.workspaceId,
            filespaceId: params.filespaceId,
            nodeId: nodeId,
        };
    }
    buildWasmFileSystemParams(params) {
        return {
            workspaceConfigKey: params.decryptedWorkspaceCommonKey,
        };
    }
    buildWasmPermissionManagerParams(principalsCrypto, isAdmin) {
        return {
            permissionsCrypto: principalsCrypto,
            isAdmin: isAdmin,
        };
    }
    buildWasmLogStoreParams(bootstrapSettings) {
        this.bucketName = this.getBucketNameSetting(bootstrapSettings);
        this.objectPrefix = this.getObjectPrefixSetting(bootstrapSettings);
        return {
            bucketName: this.bucketName,
            objectPrefix: this.objectPrefix,
        };
    }
    buildWasmFileStoreParams(bootstrapSettings) {
        return {
            stuckStateInterval: this.getStateIntervalSetting(bootstrapSettings),
        };
    }
    buildWasmAuditorParams(params) {
        return {
            userId: params.principalId,
            userName: params.principalName,
            deviceHostName: this.platformInfo.webBrowser ?? getDeviceHostName(),
            deviceOsName: this.platformInfo.platform,
            deviceOsVersion: this.platformInfo.platformVersion,
            legacyFilespaceName: params.legacyFilespaceName,
        };
    }
    getBucketNameSetting(settings) {
        const bucketName = this.getSettingFromList(settings, SettingName.BUCKET_NAME);
        return bucketName.effective().asString();
    }
    getObjectPrefixSetting(settings) {
        const objectPrefix = this.getSettingFromList(settings, SettingName.OBJECT_PREFIX);
        return objectPrefix.effective().asString();
    }
    getStateIntervalSetting(settings) {
        const stuckStateIntervalSeconds = this.getSettingFromList(settings, SettingName.STUCK_STATE_INTERVAL_SETTING);
        return stuckStateIntervalSeconds.effective().asUint64();
    }
    getBlockSizeSetting(bootstrapSettings) {
        const blockSize = this.getSettingFromList(bootstrapSettings, SettingName.BLOCK_SIZE);
        return blockSize.effective().asUint32() * 1024;
    }
    async setUp(filespaceId, remoteEndpoint, principalName, decryptedWorkspaceConfigKey) {
        this.log.debug({ 'filespace.fsId': filespaceId, endpoint: remoteEndpoint }, 'Setting up Filespace...');
        this.filespaceId = filespaceId;
        await this.setUpWebSocketClient(remoteEndpoint);
        this.serverSession = this.createServerSession();
        this.nodeId = await this.setUpNode();
        const epochManager = this.setUpEpochManager(this.nodeId);
        await this.setUpWasmMetadataModules(epochManager);
        await this.setUpWasmLogging();
        this.setUpTelemetryClient(this.wasm.metaStore(), principalName, this.nodeId);
        this.setUpAuthorizer();
        await this.setUpConfigManager(this.wasm.metaStore(), decryptedWorkspaceConfigKey);
        this.setUpKeyRangeLocking();
        await epochManager.handshake();
        this.log.debug({ 'filespace.fsId': filespaceId, endpoint: remoteEndpoint }, 'Filespace setup finished.');
    }
    async setUpWasmLogging() {
        const wasmLevel = logLevelToWasmLogLevel(Logging.getRootLogLevel());
        await this.wasm?.setLogLevel(wasmLevel);
    }
    setUpKeyRangeLocking() {
        this.keyRangeLockingClient = createKeyRangeLockingClient(this.filespaceId, this.webSocketClient, this.serverSession);
        this.statistics.keyRangeLocking = this.keyRangeLockingClient.stats();
    }
    createServerSession() {
        const serverSessionProxy = new ServerSessionProxy(this.filespaceId, this.webSocketClient);
        const serverSession = new ServerSession(serverSessionProxy, this.idService);
        this.statistics.session = serverSession.stats();
        return serverSession;
    }
    async setUpNode() {
        const proxy = new AtomicCounterProxy(this.filespaceId, this.webSocketClient, this.serverSession);
        this.node = createNode(proxy, this.storage);
        this.statistics.node = this.node.stats();
        return await this.node.getNodeIndex();
    }
    setUpEpochManager(nodeId) {
        const proxy = new EpochProxy(this.filespaceId, this.webSocketClient, this.serverSession);
        const epochManager = createEpochManager({
            nodeId: nodeId,
            proxy: proxy,
            storage: this.storage,
            errorHandler: async () => {
                await this.doUnlink();
            },
        });
        this.statistics.epoch = epochManager.stats();
        return epochManager;
    }
    setUpPrincipalCryptoProvider(userCrypto) {
        const principalCryptoProvider = createPrincipalCryptoProvider(this.idService, userCrypto);
        return principalCryptoProvider;
    }
    async setUpWasmMetadataModules(epochManager) {
        // The fsVersion/format is not known at this point, use the feature flag to determine what to load
        this.wasm = await createClientWasm();
        const metaStoreProxy = new MetaStoreProxy(this.filespaceId, this.webSocketClient, epochManager, this.serverSession);
        this.statistics.metaStore = metaStoreProxy.stats();
        await this.wasm.setUpMetadataModules(this.nodeId, metaStoreProxy);
    }
    getPrincipalsRsaKeys = async (principalIds) => {
        const principalKeyPairs = await this.idService.getPrincipalsCrypto(principalIds);
        const result = [];
        for (const principalId of Object.keys(principalKeyPairs)) {
            const cryptoKeys = principalKeyPairs[principalId];
            result.push({
                principalId: principalId,
                decryptedPrivateKeyPem: await exportPrivateCryptoKeyToPem(cryptoKeys.privateKey),
                publicKeyPem: await exportPublicCryptoKeyToPem(cryptoKeys.publicKey),
            });
        }
        return result;
    };
    async setUpConfigManager(metaStore, decryptedWorkspaceConfigKey) {
        const tuple = this.wasm.tuple();
        const keyValueHelpers = this.wasm.keyValueHelpers();
        const configWasm = this.wasm.config();
        const authorizer = this.authorizer;
        try {
            this.config = await createConfigManager({
                metaStore,
                tuple,
                keyValueHelpers,
                configWasm,
                authorizer,
                decryptedWorkspaceConfigKey,
            });
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof MissingConfigKey);
            throw new InvalidFilespaceStateError(`Trying to link to an unitilialized filespace: ${err.message}!`);
        }
    }
    setUpTelemetryClient(metaStoreWasm, principalName, nodeId) {
        this.telemetry = createTelemetryClient({
            nodeId: nodeId,
            filespaceId: this.filespaceId,
            username: principalName,
            metaStore: metaStoreWasm,
            keyValueHelpers: this.wasm.keyValueHelpers(),
        });
    }
    setUpAuthorizer() {
        this.authorizer = createAuthorizer(this.idService, this.telemetry);
    }
    getSettingFromList(settings, name) {
        const setting = getSettingFromList(settings, name);
        if (isNil(setting)) {
            throw new InvalidStateError(`Setting ${name} not found!`);
        }
        return setting;
    }
    async tearDown() {
        this.log.debug({ 'filespace.fsId': this.filespaceId }, 'Tearing down...');
        await this.tearDownFileStore();
        this.tearDownFileNodeManager();
        this.tearDownFileSystem();
        this.tearDownSettingsCache();
        this.tearDownObjectStore();
        await this.tearDownWebSocketConnection();
        this.tearDownWasm();
        this.nodeId = undefined;
        this.node = undefined;
        this.idService = undefined;
        this.bucketName = undefined;
        this.objectPrefix = undefined;
        this.permissions = undefined;
        this.telemetry = undefined;
        this.authorizer = undefined;
        this.keyRangeLockingClient = undefined;
        this.directLinksManager = undefined;
        this.linkedMode = OperationMode.READ_WRITE;
        this.log.debug({ 'filespace.fsId': this.filespaceId }, 'Tear down finished.');
        this.filespaceId = undefined;
    }
    async tearDownWebSocketConnection() {
        if (isNil(this.webSocketClient)) {
            return;
        }
        try {
            await this.webSocketClient.disconnect();
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof Error);
            this.log.error({ 'filespace.fsId': this.filespaceId, errorMessage: err.message }, 'Failed to disconnect WebSocket client!');
        }
        this.webSocketClient = undefined;
    }
    tearDownWasm() {
        if (isNil(this.wasm)) {
            return;
        }
        this.wasm.tearDown();
        this.wasm = undefined;
    }
    async setUpObjectStore(bootstrapSettings) {
        this.log.debug({ 'filespace.fsId': this.filespaceId }, 'Setting up object store...');
        const objectStoreConfig = buildObjectStoreConfig(bootstrapSettings, this.config);
        this.objectStore = await buildClientObjectStore(objectStoreConfig);
        this.httpStore = createHttpStore();
        this.statistics.objectStore = this.objectStore.stats();
        this.log.debug({ 'filespace.fsId': this.filespaceId }, 'Object store set up!');
    }
    tearDownObjectStore() {
        if (isNil(this.objectStore)) {
            return;
        }
        this.objectStore.stop();
        this.objectStore = undefined;
    }
    async tearDownFileStore() {
        if (isNil(this.fileStore)) {
            return;
        }
        await this.fileStore.stop();
        await this.statsFlusher?.stop();
        this.fileStore = undefined;
        this.statsFlusher = undefined;
    }
    tearDownFileNodeManager() {
        if (isNil(this.fileNodeManager)) {
            return;
        }
        this.fileNodeManager.stop();
        this.fileNodeManager = undefined;
    }
    tearDownFileSystem() {
        if (isNil(this.fs)) {
            return;
        }
        this.fs.stop();
        this.fs = undefined;
    }
    tearDownSettingsCache() {
        if (isNil(this.settingsCache)) {
            return;
        }
        this.settingsCache.stop();
        this.settingsCache = undefined;
    }
    async tearDownSynchronizers() {
        const tearDownPromises = [];
        if (this.permissionsSynchronizer) {
            tearDownPromises.push(this.permissionsSynchronizer.stop());
        }
        if (this.accessModeSynchronizer) {
            tearDownPromises.push(this.accessModeSynchronizer.stop());
        }
        await Promise.all(tearDownPromises);
        this.permissionsSynchronizer = undefined;
        this.accessModeSynchronizer = undefined;
    }
    createGetObjectHandler() {
        return async (bucketName, objectKey, offset, size) => {
            return await this.objectStore.getObject(bucketName, objectKey, offset, size);
        };
    }
}
